import React, { useState, useEffect } from 'react'
import { mealPlanApi, dishLogApi } from '../lib/api.js'

const DAYS_FR = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche']
const MEAL_LABEL = { breakfast: 'Petit-déj', lunch: 'Déjeuner', dinner: 'Dîner', snack: 'Snack' }

/** Returns true if today is Friday evening (≥18h), Saturday or Sunday */
function isEndOfWeek() {
  const d = new Date()
  const dow = d.getDay() // 0=Sun, 5=Fri, 6=Sat
  if (dow === 6 || dow === 0) return true
  if (dow === 5 && d.getHours() >= 18) return true
  return false
}

function getMonday(offset = 0) {
  const d = new Date()
  const day = d.getDay()
  const diff = d.getDate() - day + (day === 0 ? -6 : 1) + offset * 7
  d.setDate(diff)
  return d.toISOString().slice(0, 10)
}

/** Star rating sub-component */
function StarRating({ value, onChange }) {
  const [hovered, setHovered] = useState(0)
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          onMouseEnter={() => setHovered(star)}
          onMouseLeave={() => setHovered(0)}
          className="text-xl leading-none focus:outline-none transition-transform hover:scale-110"
          aria-label={`${star} étoile${star > 1 ? 's' : ''}`}
        >
          <span className={(hovered || value) >= star ? 'text-amber-400' : 'text-gray-300'}>★</span>
        </button>
      ))}
    </div>
  )
}

/**
 * WeeklyReview modal — appears automatically Friday evening / weekend
 * Shows all planned meals with:
 *   ☑ checkbox: "j'ai préparé ce plat"
 *   ★ star rating (1-5) per prepared dish
 *
 * Props:
 *   onClose: () => void
 */
export function WeeklyReview({ onClose }) {
  const [slots, setSlots] = useState([])
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState(null)

  // Per-slot state: { [slotId]: { checked: bool, rating: 0-5, comment: '' } }
  const [reviews, setReviews] = useState({})

  const weekStart = getMonday(0) // current week

  useEffect(() => {
    async function load() {
      try {
        const plan = await mealPlanApi.get(weekStart)
        const planSlots = plan?.slots ?? []
        setSlots(planSlots)

        // Initialise review state — pre-check slots already marked prepared
        const init = {}
        for (const s of planSlots) {
          init[s.id] = {
            checked: s.status === 'prepared',
            rating: 0,
            comment: '',
          }
        }
        setReviews(init)
      } catch (e) {
        setError('Impossible de charger le plan de la semaine.')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [weekStart])

  function toggle(slotId) {
    setReviews((prev) => ({
      ...prev,
      [slotId]: { ...prev[slotId], checked: !prev[slotId].checked },
    }))
  }

  function setRating(slotId, rating) {
    setReviews((prev) => ({ ...prev, [slotId]: { ...prev[slotId], rating } }))
  }

  function setComment(slotId, comment) {
    setReviews((prev) => ({ ...prev, [slotId]: { ...prev[slotId], comment } }))
  }

  async function handleSubmit() {
    setSubmitting(true)
    try {
      const prepared = slots
        .filter((s) => reviews[s.id]?.checked)
        .map((s) => ({
          slotId: s.id,
          name: s.custom_meal ?? s.name ?? 'Repas',
          rating: reviews[s.id]?.rating || null,
          comment: reviews[s.id]?.comment || null,
        }))

      // POST to backend weekly-review endpoint
      const res = await fetch('/api/meal-plans/weekly-review', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Id': localStorage.getItem('betina_user_id') ?? 'default-user',
        },
        body: JSON.stringify({ weekStart, prepared }),
      })

      if (!res.ok) throw new Error(await res.text())
      setDone(true)
    } catch (e) {
      setError('Erreur lors de la sauvegarde. Réessaie.')
    } finally {
      setSubmitting(false)
    }
  }

  // Group slots by day
  const byDay = {}
  for (const slot of slots) {
    const day = slot.day_of_week ?? 0
    if (!byDay[day]) byDay[day] = []
    byDay[day].push(slot)
  }

  const checkedCount = Object.values(reviews).filter((r) => r.checked).length

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="px-5 pt-5 pb-3 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">🍽️ Bilan de la semaine</h2>
            <p className="text-xs text-gray-500 mt-0.5">Coche les plats que tu as préparés et note-les</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none p-1"
            aria-label="Fermer"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-5 py-4">
          {loading && (
            <div className="flex justify-center py-10">
              <div className="w-6 h-6 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {!loading && done && (
            <div className="text-center py-12">
              <div className="text-5xl mb-3">🎉</div>
              <p className="font-semibold text-gray-800 text-lg">Super semaine !</p>
              <p className="text-sm text-gray-500 mt-1">
                {checkedCount} plat{checkedCount > 1 ? 's' : ''} enregistré{checkedCount > 1 ? 's' : ''}.
              </p>
              <button
                onClick={onClose}
                className="mt-5 px-5 py-2 bg-emerald-500 text-white rounded-xl text-sm font-medium hover:bg-emerald-600 transition-colors"
              >
                Fermer
              </button>
            </div>
          )}

          {!loading && !done && slots.length === 0 && (
            <div className="text-center py-10 text-gray-400 text-sm">
              Aucun repas planifié cette semaine.
            </div>
          )}

          {!loading && !done && slots.length > 0 && (
            <div className="space-y-5">
              {Object.entries(byDay)
                .sort(([a], [b]) => Number(a) - Number(b))
                .map(([day, daySlots]) => (
                  <div key={day}>
                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">
                      {DAYS_FR[Number(day)]}
                    </h3>
                    <div className="space-y-2">
                      {daySlots.map((slot) => {
                        const rev = reviews[slot.id] ?? { checked: false, rating: 0, comment: '' }
                        const mealName = slot.custom_meal ?? '—'
                        return (
                          <div
                            key={slot.id}
                            className={`rounded-xl border transition-colors p-3 ${
                              rev.checked
                                ? 'border-emerald-300 bg-emerald-50'
                                : 'border-gray-200 bg-white'
                            }`}
                          >
                            {/* Top row: checkbox + name + meal type */}
                            <label className="flex items-start gap-3 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={rev.checked}
                                onChange={() => toggle(slot.id)}
                                className="mt-0.5 h-4 w-4 rounded accent-emerald-500 cursor-pointer flex-shrink-0"
                              />
                              <div className="flex-1 min-w-0">
                                <p
                                  className={`text-sm font-medium leading-tight ${
                                    rev.checked ? 'text-emerald-800' : 'text-gray-700'
                                  }`}
                                >
                                  {mealName}
                                </p>
                                <p className="text-xs text-gray-400 mt-0.5">
                                  {MEAL_LABEL[slot.meal_type] ?? slot.meal_type}
                                </p>
                              </div>
                            </label>

                            {/* Rating — only shown when checked */}
                            {rev.checked && (
                              <div className="mt-3 pl-7 space-y-2">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-gray-500 w-12">Note :</span>
                                  <StarRating value={rev.rating} onChange={(r) => setRating(slot.id, r)} />
                                  {rev.rating > 0 && (
                                    <span className="text-xs text-gray-400">{rev.rating}/5</span>
                                  )}
                                </div>
                                <input
                                  type="text"
                                  value={rev.comment}
                                  onChange={(e) => setComment(slot.id, e.target.value)}
                                  placeholder="Un commentaire ? (optionnel)"
                                  className="w-full text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-emerald-400 text-gray-600"
                                />
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                ))}
            </div>
          )}

          {error && (
            <p className="text-xs text-red-500 mt-3 text-center">{error}</p>
          )}
        </div>

        {/* Footer */}
        {!done && !loading && slots.length > 0 && (
          <div className="px-5 py-4 border-t border-gray-100">
            <p className="text-xs text-gray-400 text-center mb-3">
              {checkedCount} plat{checkedCount > 1 ? 's' : ''} coché{checkedCount > 1 ? 's' : ''}
            </p>
            <button
              onClick={handleSubmit}
              disabled={submitting || checkedCount === 0}
              className="w-full py-3 bg-emerald-500 text-white font-semibold rounded-xl text-sm hover:bg-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Sauvegarde…
                </>
              ) : (
                '✓ Valider le bilan'
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Hook: returns true if we should prompt the end-of-week review.
 * Suppressed for 7 days after last submission (via localStorage).
 */
export function useWeeklyReviewPrompt() {
  const [shouldShow, setShouldShow] = useState(false)

  useEffect(() => {
    if (!isEndOfWeek()) return

    const lastKey = 'betina_last_review'
    const last = localStorage.getItem(lastKey)
    if (last) {
      const daysSince = (Date.now() - Number(last)) / 86400000
      if (daysSince < 5) return // already reviewed this week
    }

    // Small delay so it doesn't pop before page loads
    const t = setTimeout(() => setShouldShow(true), 1500)
    return () => clearTimeout(t)
  }, [])

  function dismiss() {
    localStorage.setItem('betina_last_review', String(Date.now()))
    setShouldShow(false)
  }

  return { shouldShow, dismiss }
}
